import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Send } from 'lucide-react';
import { useState } from 'react';
import { useData } from '@happyspace/sdk/data';
import SectionHeading from '../components/SectionHeading';

export default function Contact() {
  const { insert, loading } = useData('contact_submissions');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    await insert({ ...form, submittedAt: new Date().toISOString() });
    setSubmitted(true);
    setForm({ name: '', email: '', message: '' });
  };

  return (
    <>
      <Helmet>
        <title>Order & Visit Biryani2Go — Omaha & Council Bluffs Location, Hours & Delivery</title>
        <meta name="description" content="Biryani2Go Omaha & Council Bluffs: dum-cooked biryani for pickup, delivery & dine-in. 2750 S Preston Rd, Omaha. Open 11 AM–10 PM. Call for orders. Indian restaurant near me." />
        <link rel="canonical" href="https://biryani2go.us/contact" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://biryani2go.us"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Contact & Location",
                "item": "https://biryani2go.us/contact"
              }
            ]
          }
        ` }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Biryani2Go",
            "description": "Dum-cooked biryani takeout and delivery in Omaha and Council Bluffs. Fresh handmade biryani, dosas, samosas — sealed and steamed to order.",
            "url": "https://biryani2go.us",
            "telephone": "+1-402-XXX-XXXX",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "2750 S Preston Rd, STE 111",
              "addressLocality": "Omaha",
              "addressRegion": "NE",
              "postalCode": "68131",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "41.2261",
              "longitude": "-95.9701"
            },
            "image": "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/485ee0f5-162d-499f-b44f-69bc80fbabf9/1785626036737-vd8ge.png",
            "priceRange": "$",
            "servesCuisine": "Indian",
            "areaServed": ["Omaha, NE", "Council Bluffs, IA"],
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                "opens": "11:00",
                "closes": "22:00"
              }
            ],
            "sameAs": [
              "https://www.facebook.com/biryani2go",
              "https://www.instagram.com/biryani2go"
            ]
          }
        ` }} />
      </Helmet>

      <section className="bg-maroon py-16 md:py-20">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8">
          <SectionHeading
            eyebrow="Get In Touch"
            title="Visit & Contact"
            description="Questions about an order, catering, or just want to say hello? Reach out below."
          />
        </div>
      </section>

      <section className="bg-surface py-16 md:py-24">
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div className="card-tin p-6 flex gap-4 items-start">
              <div className="w-11 h-11 rounded-full bg-ember/10 flex items-center justify-center shrink-0">
                <MapPin size={20} className="text-ember" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-maroon mb-1">Location</h3>
                <p className="text-sm text-ink-2">2750 S Preston Rd, STE 111<br />Omaha, NE 68131</p>
              </div>
            </div>
            <div className="card-tin p-6 flex gap-4 items-start">
              <div className="w-11 h-11 rounded-full bg-ember/10 flex items-center justify-center shrink-0">
                <Phone size={20} className="text-ember" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-maroon mb-1">Phone</h3>
                <a href="tel:+1-402-000-0000" className="text-sm text-ember font-semibold hover:underline">+1 (402) 000-0000</a>
              </div>
            </div>
            <div className="card-tin p-6 flex gap-4 items-start">
              <div className="w-11 h-11 rounded-full bg-ember/10 flex items-center justify-center shrink-0">
                <Clock size={20} className="text-ember" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-lg text-maroon mb-1">Hours</h3>
                <p className="text-sm text-ink-2">Mon–Sun: 11 AM – 10 PM<br /><strong>Order Online Anytime</strong></p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="card-tin p-8"
          >
            {submitted ? (
              <div className="text-center py-10">
                <p className="font-display text-2xl text-maroon mb-2">Message sent!</p>
                <p className="text-ink-2">We'll get back to you soon.</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 btn-secondary">Send Another</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-ink-2 mb-1.5 block">Name</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="field"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink-2 mb-1.5 block">Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="field"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink-2 mb-1.5 block">Message</label>
                  <textarea
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="field !h-32 py-3 resize-none"
                    placeholder="How can we help?"
                  />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                  <Send size={17} /> {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
}
